import React, { useMemo } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import {
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiChevronUp,
  FiSearch,
} from 'react-icons/fi';
import {
  SaktiPaginationMeta,
  SaktiTableColumn,
  SaktiTableInfo,
  SaktiTableRow,
} from '../types';
import { SAKTI_UI_SIZE } from '../uiSizes';
import { SortDir } from '../hooks/useSaktiDataViewer';

interface SaktiDataViewerProps {
  tables: SaktiTableInfo[];
  loadingTables: boolean;
  tablesError: string | null;
  selectedTable: string | null;
  columns: SaktiTableColumn[];
  rows: SaktiTableRow[];
  pagination: SaktiPaginationMeta;
  loadingRows: boolean;
  rowsError: string | null;
  searchInput: string;
  sortBy: string | null;
  sortDir: SortDir;
  qualifiedName: (table: SaktiTableInfo) => string;
  onSelectTable: (table: string) => void;
  onSearchChange: (value: string) => void;
  onToggleSort: (column: string) => void;
  onPageChange: (page: number) => void;
}

// Render any JSON cell value safely — objects/arrays would otherwise crash React.
const formatCell = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

const getPaginationButtons = (currentPage: number, totalPages: number): number[] => {
  const visibleRange = 3;
  const startPage = Math.max(1, currentPage - 1);
  const endPage = Math.min(totalPages, startPage + visibleRange - 1);
  const pages: number[] = [];
  for (let page = startPage; page <= endPage; page += 1) {
    pages.push(page);
  }
  return pages;
};

const SaktiDataViewer: React.FC<SaktiDataViewerProps> = ({
  tables,
  loadingTables,
  tablesError,
  selectedTable,
  columns,
  rows,
  pagination,
  loadingRows,
  rowsError,
  searchInput,
  sortBy,
  sortDir,
  qualifiedName,
  onSelectTable,
  onSearchChange,
  onToggleSort,
  onPageChange,
}) => {
  // Group table options by schema for the picker's <optgroup>s.
  // const tablesBySchema = useMemo(() => {
  //   const grouped: Record<string, SaktiTableInfo[]> = {};
  //   tables.forEach((table) => {
  //     (grouped[table.schema] ??= []).push(table);
  //   });
  //   return grouped;
  // }, [tables]);

  const pageButtons = getPaginationButtons(pagination.page, pagination.total_pages);

  return (
    <Box
      bg="white"
      borderRadius={SAKTI_UI_SIZE.cardRadius}
      borderWidth="1px"
      borderColor="gray.200"
      p={SAKTI_UI_SIZE.cardPadding}
      minW={0}
    >
      <VStack align="stretch" spacing={4}>
        <VStack align="start" spacing={0}>
          <Text fontWeight="bold" fontSize={SAKTI_UI_SIZE.tabTitle} color="gray.900">
            Data Master
          </Text>
          <Text fontSize="xs" color="gray.500">
            Lihat data master
          </Text>
        </VStack>

        {tablesError && (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Text fontSize="sm">{tablesError}</Text>
          </Alert>
        )}

        {/* Controls */}
        <HStack spacing={3} flexWrap="wrap" align="end">
          <Box minW={{ base: 'full', md: '320px' }}>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Tabel
            </Text>
            <Skeleton isLoaded={!loadingTables} borderRadius="md">
              <Select
                placeholder="Pilih tabel..."
                value={selectedTable ?? ''}
                onChange={(event) => onSelectTable(event.target.value)}
                bg="white"
                size="sm"
              >
                {tables.map((table) => {
                  const name = qualifiedName(table);
                  return (
                    <option key={name} value={name}>
                          {name}
                        </option>
                      );
                    })}
              </Select>
            </Skeleton>
          </Box>

          <Box flex={1} minW={{ base: 'full', md: '240px' }}>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Cari
            </Text>
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <Icon as={FiSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Cari di semua kolom..."
                value={searchInput}
                onChange={(event) => onSearchChange(event.target.value)}
                isDisabled={!selectedTable}
                bg="white"
                borderRadius="md"
              />
            </InputGroup>
          </Box>
        </HStack>

        {rowsError && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Text fontSize="sm">{rowsError}</Text>
          </Alert>
        )}

        {!selectedTable ? (
          <Box py={10} textAlign="center">
            <Text color="gray.500" fontSize="sm">
              Pilih tabel untuk menampilkan data.
            </Text>
          </Box>
        ) : (
          <Skeleton isLoaded={!loadingRows} borderRadius={SAKTI_UI_SIZE.cardRadius} minH="120px">
            <Box
              borderRadius={SAKTI_UI_SIZE.cardRadius}
              overflow="hidden"
              borderWidth="1px"
              borderColor="gray.200"
            >
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead bg="customGreen.500">
                    <Tr>
                      {columns.map((column) => {
                        const isSorted = sortBy === column.name;
                        return (
                          <Th
                            key={column.name}
                            color="white"
                            fontSize={SAKTI_UI_SIZE.tableHeaderFont}
                            py={2.5}
                            cursor="pointer"
                            whiteSpace="nowrap"
                            onClick={() => onToggleSort(column.name)}
                            _hover={{ bg: 'customGreen.600' }}
                          >
                            <HStack spacing={1}>
                              <Text>{column.name}</Text>
                              {isSorted && (
                                <Icon as={sortDir === 'asc' ? FiChevronUp : FiChevronDown} />
                              )}
                            </HStack>
                          </Th>
                        );
                      })}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows.length === 0 ? (
                      <Tr>
                        <Td colSpan={Math.max(columns.length, 1)}>
                          <Text color="gray.500" textAlign="center" py={4} fontSize="sm">
                            Tidak ada data.
                          </Text>
                        </Td>
                      </Tr>
                    ) : (
                      rows.map((row, rowIndex) => (
                        <Tr key={rowIndex}>
                          {columns.map((column) => (
                            <Td
                              key={column.name}
                              fontSize={SAKTI_UI_SIZE.tableCellFont}
                              py={2}
                              whiteSpace="nowrap"
                              maxW="320px"
                              overflow="hidden"
                              textOverflow="ellipsis"
                              title={formatCell(row[column.name])}
                            >
                              {formatCell(row[column.name])}
                            </Td>
                          ))}
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>

              <HStack
                px={4}
                py={3}
                bg="customGreen.500"
                color="white"
                justify="space-between"
                flexWrap="wrap"
                gap={3}
              >
                <Text fontSize="xs" fontWeight="semibold" letterSpacing="wider">
                  MENAMPILKAN {rows.length} DARI{' '}
                  {pagination.total_items.toLocaleString('id-ID')} (HAL {pagination.page}/
                  {Math.max(pagination.total_pages, 1)})
                </Text>

                <HStack spacing={1}>
                  <IconButton
                    aria-label="Halaman sebelumnya"
                    icon={<FiChevronLeft />}
                    size="sm"
                    variant="outline"
                    borderColor="whiteAlpha.700"
                    color="white"
                    isDisabled={pagination.page <= 1}
                    onClick={() => onPageChange(pagination.page - 1)}
                  />

                  {pageButtons.map((page) => (
                    <Button
                      key={page}
                      size="sm"
                      variant={page === pagination.page ? 'solid' : 'outline'}
                      bg={page === pagination.page ? 'white' : 'transparent'}
                      color={page === pagination.page ? 'customGreen.500' : 'white'}
                      borderColor="whiteAlpha.700"
                      onClick={() => onPageChange(page)}
                    >
                      {page}
                    </Button>
                  ))}

                  <IconButton
                    aria-label="Halaman berikutnya"
                    icon={<FiChevronRight />}
                    size="sm"
                    variant="outline"
                    borderColor="whiteAlpha.700"
                    color="white"
                    isDisabled={pagination.page >= pagination.total_pages}
                    onClick={() => onPageChange(pagination.page + 1)}
                  />
                </HStack>
              </HStack>
            </Box>
          </Skeleton>
        )}
      </VStack>
    </Box>
  );
};

export default SaktiDataViewer;
